import {E} from "js-vextensions";
import {CreateAccessor, GetAsync, observer_mgl} from "mobx-graphlink";
import React, {useMemo} from "react";
import {Button, Row} from "react-vcomponents";
import {ES, RunInAction} from "web-vcore";
import {UpdateMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {GetMapObjects} from "../../../../Server/DB/mapObjects.js";
import {GetDisplayTitleForObj, GetObjDescendants, ResolveObjPosInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {store} from "../../../../Store/index.js";
import {GetMapState} from "../../../../Store/main/maps/$mapState.js";
import {DoesObjectPassFilter, IsFilterMeaningful} from "../../../../Utils/Filters/OutlinerFilter.js";
import {Rect, Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {ObjectsOrderedForOutlinerUI} from "../OutlinerUI.js";
import {ObjectEntryUI_MenuStub} from "./ObjectEntryUI_Menu.js";
import {GetCurrentMapControls} from "../../../../Store/main/maps.js";

export const ObjectEntryUI = observer_mgl((props: {map: DMap, obj: MapObject, ancestors: string[]})=>{
	const {map, obj, ancestors} = props;

	// for now, just get the whole objects-list and filter from there; will change approach once we have many objects
	//const childObjects = GetMapObjects(map.id, obj.id);
	const objects = GetMapObjects(map.id);
	const childObjects = ObjectsOrderedForOutlinerUI(objects.filter(a=>a.parent == obj.id));
	const childAncestors = useMemo(()=>[...ancestors, obj.id], [ancestors, obj.id]);

	return (
		<>
			<ObjectEntryBox {...{map, obj, ancestors, childObjects}}/>
			{childObjects.map(childObject=>{
				return <ObjectEntryUI {...{map}} key={childObject.id} obj={childObject} ancestors={childAncestors}/>;
			})}
		</>
	);
});

export const GetViewportWorldRect = CreateAccessor((mapID: string)=>{
	const mapState = GetMapState(mapID);
	if (!mapState) return null;

	// simply access these fields, so this accessor recalculates whenever these values change
	mapState.camPos_500ms; // eslint-disable-line
	mapState.camZoom_500ms; // eslint-disable-line

	const viewportEl = store.main.maps.mapSurface_3JSContainer;
	if (!viewportEl || !viewportEl.isConnected) return null;
	const controls_DOM = GetCurrentMapControls()?.DOM;
	if (!controls_DOM) return null;

	const rect_raw = viewportEl.getBoundingClientRect();
	const rect = Rect.FromLTWH(rect_raw, false);
	const topLeft_world = controls_DOM.ClientPosToWorldPos(new Vec2(rect.Left, rect.Top));
	const bottomRight_world = controls_DOM.ClientPosToWorldPos(new Vec2(rect.Right, rect.Bottom));
	if (!topLeft_world || !bottomRight_world) return null;
	const width_world = bottomRight_world.x - topLeft_world.x;
	const height_world = topLeft_world.y - bottomRight_world.y; // y0 is bottom in world space
	const rect_world = new Rect(
		topLeft_world.x,
		bottomRight_world.y, // y0 is bottom in world space
		width_world,
		height_world,
		true,
	);
	return rect_world;
});
export const GetObjDistFromViewport_MinOnOneDimension = CreateAccessor((mapID: string, obj: MapObject)=>{
	const viewportRect = GetViewportWorldRect(mapID);
	if (!viewportRect) return Infinity;
	const objPosInWorld = ResolveObjPosInWorld(obj);
	if (!objPosInWorld) return Infinity;
	const objPoint = objPosInWorld.ToVec2();
	const objRect = new Rect(objPoint, Vec2.zero, false);
	if (viewportRect.Intersects(objRect)) return 0;
	return Math.min(
		objPoint.x.Distance(viewportRect.Left),
		objPoint.x.Distance(viewportRect.Right),
		objPoint.y.Distance(viewportRect.Top),
		objPoint.y.Distance(viewportRect.Bottom),
	);
});

export const ObjectEntryBox = observer_mgl((props: {map: DMap, obj: MapObject, ancestors: string[], childObjects: MapObject[]})=>{
	const {map, obj, ancestors, childObjects} = props;
	const title = GetDisplayTitleForObj(obj, "outliner");
	const depth = ancestors.length;
	const mapState = GetMapState(map.id)!;
	const objIsExpanded = mapState.expandedObjects[obj.id];

	const shouldBeVisible = (()=>{
		const isVisibleOrSelected = GetObjDistFromViewport_MinOnOneDimension(map.id, obj) <= 0 || mapState.selectedObjects.includes(obj.id);
		const ignoreAncestorCollapses = (
			(mapState.outliner.filter_onlyVisibleOrSelected && isVisibleOrSelected) ||
			(mapState.outliner.filter_matchPassThrough && IsFilterMeaningful(mapState.outliner.filter_text))
		);

		// level 1: visible if all ancestors are expanded, OR search state lets ancestor-collapses be ignored
		const allAncestorsAreExpanded = ancestors.All(ancestorID=>mapState.expandedObjects[ancestorID]);
		let result = allAncestorsAreExpanded || ignoreAncestorCollapses;

		// level 2: filter out entries that aren't actually a match for the text-filter
		const objMatchesTextFilter = mapState.outliner.filter_text.length == 0 ? true :
			DoesObjectPassFilter(obj, mapState.outliner.filter_text, GetDisplayTitleForObj(obj, "outliner"));
		if (!objMatchesTextFilter) result = false;

		return result;
	})();
	if (!shouldBeVisible) return null;

	return (
		<Row data-obj-id={obj.id} style={E(
			{padding: `5px 5px 5px ${5 + (depth * 10)}px`},
			mapState.selectedObjects.includes(obj.id) && {backgroundColor: "rgba(29,49,77,1)", color: "rgba(231,106,1,1)"},
			mapState.selectedObjects.includes(obj.id) && mapState.activeObject == obj.id && {backgroundColor: "rgba(77,100,144,1)", color: "rgba(244,170,46,1)"},
		)} onClick={e=>{
			e.stopPropagation();
			RunInAction("ObjectEntryUI.onClick", ()=>{
				// match blender behavior
				if (e.ctrlKey) {
					if (mapState.selectedObjects.includes(obj.id)) {
						if (mapState.activeObject == obj.id) {
							mapState.selectedObjects = mapState.selectedObjects.filter(a=>a != obj.id);
						} else {
							mapState.activeObject = obj.id;
						}
					} else {
						mapState.selectedObjects = mapState.selectedObjects.concat([obj.id]);
						mapState.activeObject = obj.id;
					}
				} else {
					mapState.selectedObjects = [obj.id];
					mapState.activeObject = obj.id;
				}
			});
		}}>
			<ObjectEntryUI_MenuStub {...{obj: obj, map, mapState}}/>
			<div className={`mdi ${objIsExpanded ? "mdi-chevron-down" : "mdi-chevron-right"}`} style={ES(
				{display: "flex", marginLeft: 5, paddingLeft: 2, width: 20, height: 20, fontSize: "18px", borderRadius: 5, cursor: "pointer"},
				//mapState.activeObject == obj.id && {backgroundColor: "rgba(82,82,82,1)"},
				// hide expand/collapse buttons, if there are no children -- but make it still take up space (so you can discern path-depth from consistent indentation)
				childObjects.length == 0 && {opacity: 0, pointerEvents: "none"},
				// also hide the button if "Show only visible + selected" is enabled, since the expand/collapse functionality doesn't actually filter the tree in that case anyway
				mapState.outliner.filter_onlyVisibleOrSelected && {opacity: 0, pointerEvents: "none"},
			)} onClick={e=>{
				e.stopPropagation();
				RunInAction("ObjectEntryUI.toggleCollapse", async()=>{
					if (e.altKey) {
						const objDescendantsWithChildren = await GetAsync(()=>GetObjDescendants(obj, true)).WaitXOr(3000, []);
						for (const objectID of [obj.id, ...objDescendantsWithChildren.map(descendant=>descendant.id)]) {
							if (objIsExpanded) delete mapState.expandedObjects[objectID];
							else mapState.expandedObjects[objectID] = true;
						}
					} else {
						if (objIsExpanded) delete mapState.expandedObjects[obj.id];
						else mapState.expandedObjects[obj.id] = true;
					}
				});
			}}/>
			<span style={{marginLeft: 5, flex: 1}}>{title}</span>
			<Button ml={5} mt={-5} mb={-5} mdIcon="anchor" style={E(mapState.referenceObject != obj.id && {opacity: 0.5})} onClick={e=>{
				e.stopPropagation();
				RunInAction("ObjectEntryUI.clearReferenceObject", ()=>{
					if (mapState.referenceObject == obj.id) mapState.referenceObject = null;
					else mapState.referenceObject = obj.id;
				});
			}}/>
			<Button ml={5} mt={-5} mb={-5} mdIcon={obj.hideSubtree ? "eye-closed" : obj.hideSelf ? "eye-off-outline" : "eye"}
				onClick={e=>{
					e.stopPropagation();
					(async()=>{
						const currentlyHidden = obj.hideSelf || obj.hideSubtree;
						// if shift held, toggle visibility of just this one object
						if (e.shiftKey) {
							/*const descendants = await GetAsync(()=>GetObjDescendants(obj));
							for (const descendant of descendants) {
								new UpdateMapObject({id: descendant.id, updates: {visible: newVisible}}).RunOnServer();
							}*/
							if (currentlyHidden) {
								new UpdateMapObject({id: obj.id, updates: {hideSelf: null, hideSubtree: null}}).RunOnServer();
							} else {
								new UpdateMapObject({id: obj.id, updates: {hideSelf: true, hideSubtree: null}}).RunOnServer();
							}
						}
						// else, toggle visibility of this entire object subtree
						else {
							if (currentlyHidden) {
								new UpdateMapObject({id: obj.id, updates: {hideSelf: null, hideSubtree: null}}).RunOnServer();
							} else {
								new UpdateMapObject({id: obj.id, updates: {hideSelf: null, hideSubtree: true}}).RunOnServer();
							}
						}
					})();
				}}
				/*onContextMenu={e=>{
					e.preventDefault();
					// ...
				}}*//>
		</Row>
	);
});